/**
 * The composer's slash commands as text.
 *
 * A finished command is held apart from the draft and drawn as a chip.
 * Sending puts it back in front, as the text the Worker already parses
 * (`parseCommand` in worker/lib/engine/turn.ts, `modelText` in context.ts).
 * A command is a built-in one, a built-in skill (`BUILTIN_SKILLS`) or one of
 * the person's skills, by name, which the Worker runs for that message
 * (context.ts).
 */

import { BUILTIN_SKILLS, type BuiltinSkillGroup } from '@wire';

export type SlashCommand = 'image' | 'video' | 'summarise' | 'remember' | 'schedule';

/** The built-in commands' names. */
export const COMMANDS: readonly SlashCommand[] = ['image', 'video', 'summarise', 'remember', 'schedule'];

/** The built-in skills' names. */
export const BUILTIN_NAMES: readonly string[] = BUILTIN_SKILLS.map((s) => s.name);

/** Each built-in skill's icon, by its group. */
export const GROUP_ICON: Record<BuiltinSkillGroup, string> = { writing: 'pen-nib', code: 'code', work: 'briefcase' };

/**
 * A draft that opens with a whole command and then whitespace, typed or
 * pasted: the command, and the text after it. Null for anything else,
 * including a command still being typed ("/ima") and one not offered here.
 */
export function leadingCommand(
  draft: string,
  offered: readonly string[]
): { command: string; rest: string } | null {
  const m = /^\/([a-z][a-z0-9-]*)\s+/i.exec(draft);
  const name = m?.[1]?.toLowerCase();
  const command = offered.find((c) => c === name);
  return m && command ? { command, rest: draft.slice(m[0].length) } : null;
}

/**
 * A sent message that opens with one of `names`, as the Worker parses it: the
 * command alone ("/summarise") or followed by whitespace and the rest. Drawn
 * as the composer's chip in the person's message.
 */
export function sentCommand(text: string, names: readonly string[]): { command: string; rest: string } | null {
  const m = /^\/([a-z][a-z0-9-]*)(?:\s+([\s\S]*))?$/i.exec(text);
  const name = m?.[1]?.toLowerCase();
  return m && name && names.includes(name) ? { command: name, rest: m[2] ?? '' } : null;
}

/** What sending puts on the wire. */
export const commandText = (command: string | null, draft: string): string =>
  (command ? `/${command} ${draft.trim()}` : draft).trim();
